import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel, IconButton } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Build a label in the selected language. Prefixes and suffixes apply only to array leaves.
const getNodeLabel = (label, lang, value, applyAffixes = false, year) => {
  const text = label?.[lang] || label?.english || value;
  const prefix = applyAffixes ? label?.prefix?.[lang] || '' : '';
  const suffix = applyAffixes ? label?.suffix?.[lang] || '' : '';
  return `${prefix}${text}${suffix}${year ? ` (${year})` : ''}`;
};

// Return true when a clean selection contains at least one selected value.
const hasSelection = (selection) => {
  if (selection === true) return true;
  if (Array.isArray(selection)) return selection.length > 0;
  return Object.values(selection || {}).some(hasSelection);
};

// Calculate complete and indeterminate states from clean selections.
const getSelectionState = (value, selection) => {
  if (Array.isArray(value)) {
    const selected = value.filter((item) => selection?.includes(item)).length;
    return {
      checked: value.length > 0 && selected === value.length,
      indeterminate: selected > 0 && selected < value.length
    };
  }

  const keys = Object.keys(value || {});
  if (keys.length === 0) return { checked: selection === true, indeterminate: false };

  const children = keys.map((key) => getSelectionState(value[key], selection?.[key]));
  const checked = children.length > 0 && children.every((state) => state.checked);
  const partial = children.some((state) => state.checked || state.indeterminate);
  return { checked, indeterminate: !checked && partial };
};

// Create a clean selection branch without storing unchecked or internal marker keys.
const createSelection = (value, selected) => {
  if (!selected) return undefined;
  if (Array.isArray(value)) return [...value];

  const keys = Object.keys(value || {});
  if (keys.length === 0) return true;
  return keys.reduce((result, key) => {
    result[key] = createSelection(value[key], true);
    return result;
  }, {});
};

const mergePreviousSelections = (data, selections) => {
  const yearLabels = {};
  const merged = {};
  const addSelection = (value, selection, year, result, path) => {
    if (!selection) return;
    const pathKey = path.join('.');
    if (Array.isArray(value)) {
      const selectedItems = new Set(result || []);
      (selection || []).forEach((item) => {
        selectedItems.add(item);
        const itemPath = [...path, item].join('.');
        yearLabels[itemPath] = [...new Set([...(yearLabels[itemPath] || []), year])];
      });
      if (selectedItems.size > 0) Object.assign(result, [...selectedItems]);
      return;
    }

    const keys = Object.keys(value || {});
    if (keys.length === 0) {
      yearLabels[pathKey] = [...new Set([...(yearLabels[pathKey] || []), year])];
      return;
    }

    keys.forEach((key) => {
      if (!selection[key]) return;
      if (!Array.isArray(value[key]) && Object.keys(value[key] || {}).length === 0) {
        result[key] = true;
        const itemPath = [...path, key].join('.');
        yearLabels[itemPath] = [...new Set([...(yearLabels[itemPath] || []), year])];
        return;
      }
      result[key] = result[key] || (Array.isArray(value[key]) ? [] : {});
      addSelection(value[key], selection[key], year, result[key], [...path, key]);
    });
  };

  Object.keys(selections)
    .filter((year) => year !== 'no_data')
    .forEach((year) => addSelection(data, selections[year], year, merged, []));

  return { merged, yearLabels };
};

const getYearsForPath = (yearLabels, path, year, selection) => {
  const pathKey = path.join('.');
  const descendantYears = Object.entries(yearLabels)
    .filter(([key]) => key === pathKey || key.startsWith(`${pathKey}.`))
    .flatMap(([, years]) => years);
  const years = [...new Set(descendantYears)];
  return years.length > 0 ? years : (year && hasSelection(selection) ? [year] : []);
};

// Render one node and recursively render its children.
const TreeNode = ({
  nodeKey,
  value,
  label,
  labels,
  lang,
  selection,
  onChange,
  disabled,
  applyAffixes = false,
  isNested = false,
  year,
  yearLabels = {},
  path = []
}) => {
  const isLeaf = !Array.isArray(value) && Object.keys(value || {}).length === 0;
  const isBranch = !isLeaf;
  const displayYears = getYearsForPath(yearLabels, path, year, selection);
  const displayYear = displayYears.join(', ');
  const [expanded, setExpanded] = useState(false);
  const state = getSelectionState(value, selection);
  const updateSelection = (next) => onChange(nodeKey, next);

  return (
    <Box
      sx={{
        ml: isNested ? 2 : 0,
        width: '100%',
        maxWidth: '100%',
        minHeight: 40,
        gridColumn: expanded && isBranch ? '1 / -1' : 'auto',
        boxSizing: 'border-box',
        minWidth: 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
        {!isLeaf ? (
          <IconButton
            size="small"
            sx={{ width: 40, flexShrink: 0 }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 40, flexShrink: 0 }} />
        )}
        <FormControlLabel
          sx={{
            m: 0,
            maxWidth: '100%',
            minWidth: 0,
            '& .MuiFormControlLabel-label': {
              minWidth: 0,
              lineHeight: 1.3,
              overflowWrap: 'anywhere'
            }
          }}
          control={
            <Checkbox
              checked={state.checked}
              indeterminate={state.indeterminate}
              disabled={disabled}
              onChange={() => updateSelection(createSelection(value, !state.checked))}
            />
          }
          label={getNodeLabel(label?._self || label, lang, nodeKey, applyAffixes, displayYear)}
        />
      </Box>

      {!isLeaf && expanded && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            alignItems: 'flex-start',
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            columnGap: 1,
            rowGap: 0.5,
            gridColumn: '1 / -1'
          }}
        >
          {(Array.isArray(value) ? value : Object.keys(value || {})).map((child) => {
            const childValue = Array.isArray(value) ? {} : value[child];
            const childLabel = Array.isArray(value)
              ? { ...(labels?.[nodeKey] || {}), telugu: child, sanskrit: child, english: child }
              : labels?.[child] || labels?.[nodeKey]?.[child];
            return (
              <TreeNode
                key={child}
                nodeKey={child}
                value={childValue}
                label={childLabel}
                labels={labels?.[nodeKey] || labels}
                lang={lang}
                selection={Array.isArray(value) ? (selection?.includes(child) ? true : undefined) : selection?.[child]}
                onChange={(key, childSelection) => {
                  if (Array.isArray(value)) {
                    const selected = new Set(selection || []);
                    if (childSelection === undefined) selected.delete(key);
                    else selected.add(key);
                    onChange(nodeKey, [...selected]);
                  } else {
                    const next = { ...(selection || {}) };
                    if (childSelection === undefined) delete next[key];
                    else next[key] = childSelection;
                    onChange(nodeKey, next);
                  }
                }}
                disabled={disabled}
                applyAffixes={Array.isArray(value)}
                isNested
                year={year}
                yearLabels={yearLabels}
                path={[...path, child]}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// Render one year's tree and keep the parent state free of checkbox marker keys.
const YearTree = ({ data, labels, lang, selection, onChange, disabled, year, yearLabels }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 1,
      width: '100%',
      minWidth: 0,
      overflow: 'hidden'
    }}
  >
    {Object.keys(data || {}).map((key) => (
      <TreeNode
        key={key}
        nodeKey={key}
        value={data[key]}
        label={labels?.[key]}
        labels={labels}
        lang={lang}
        selection={selection?.[key]}
        onChange={(nodeKey, nodeSelection) => {
          const next = { ...(selection || {}) };
          if (nodeSelection === undefined) delete next[nodeKey];
          else next[nodeKey] = nodeSelection;
          onChange(next);
        }}
        disabled={disabled}
        year={year}
        yearLabels={yearLabels}
        path={[key]}
      />
    ))}
  </Box>
);

// Render current and previous trees using clean, year-keyed selection objects.
export default function GrandhamTreeData({
  data,
  labels,
  lang,
  selections = {},
  onSelectionsChange,
  previous = false,
  currentYear = ''
}) {
  const years = Object.keys(selections).filter((key) => key !== 'no_data');
  const hasPreviousSelections = previous && years.some((year) => hasSelection(selections[year]));
  const noDataSelected = selections.no_data === true;
  const previousTree = previous ? mergePreviousSelections(data, selections) : null;

  // Update one year without introducing unchecked or internal marker keys.
  const updateYear = (year, nextSelection) => {
    const next = { ...selections };
    if (hasSelection(nextSelection)) next[year] = nextSelection;
    else delete next[year];
    delete next.no_data;
    onSelectionsChange(next);
  };

  // Store the explicit no-data choice using its English key.
  const selectNoData = (checked) => onSelectionsChange(checked ? { no_data: true } : {});

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {previous && (
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Box sx={{ width: 40, flexShrink: 0 }} />
          <FormControlLabel
            sx={{ m: 0, minWidth: 0 }}
            control={
              <Checkbox
                checked={noDataSelected}
                disabled={hasPreviousSelections}
                onChange={(event) => selectNoData(event.target.checked)}
              />
            }
            label={getNodeLabel(
              labels?.no_data_labels || { telugu: 'ఏమీ ఇవ్వలేదు', sanskrit: 'किमपि नोक्तम्', english: 'No data' },
              lang,
              'No data'
            )}
          />
        </Box>
      )}

      {previous ? (
        years.length > 0 ? (
          <YearTree
            data={data}
            labels={labels}
            lang={lang}
            selection={previousTree.merged}
            onChange={(next) => updateYear(
              currentYear ? String(Number(currentYear) - 1) : '',
              next
            )}
            disabled={hasPreviousSelections || noDataSelected}
            yearLabels={previousTree.yearLabels}
          />
        ) : (
          <YearTree
            data={data}
            labels={labels}
            lang={lang}
            selection={{}}
            onChange={(next) => updateYear(currentYear, next)}
            disabled={noDataSelected}
            year=""
          />
        )
      ) : (
        <YearTree
          data={data}
          labels={labels}
          lang={lang}
          selection={selections[currentYear] || {}}
          onChange={(next) => onSelectionsChange({ [currentYear]: next })}
          disabled={false}
          year=""
        />
      )}
    </Box>
  );
}
